/**
 * Pausas del profesional: cuándo empiezan, cuánto aportan a la espera y cuándo
 * decir «Comiendo».
 *
 * Existe porque el mismo cálculo estaba escrito en cinco sitios —la tarjeta de
 * barbero, la estimación de la reserva, «Mis turnos», la pantalla de la TV y el
 * panel del profesional— y al corregir la incidencia #22 habrían divergido.
 * La regla equivalente en el servidor está en `functions/src/queue/helpers.ts`.
 *
 * #22: el profesional marca la comida POR ANTICIPADO, mientras sigue cortando.
 * La pausa no empieza al marcarla, sino cuando termina la cola que tiene
 * delante, y `breakStartAt` recoge ese instante.
 */
import type { BarberStatus } from '@/types';

/** Instante en que empieza la pausa, en milisegundos, o null si no hay pausa temporal con hora de vuelta. */
function inicioMs(status: BarberStatus | null | undefined, nowMs: number): number | null {
  if (!status || status.status !== 'on_break') return null;
  if (!status.breakIsTemporary || !status.breakExpectedEndAt) return null;
  // Documentos anteriores a #22 no llevan breakStartAt: se recurre al instante
  // en que se marcó, que ya pasó, de modo que se comportan como siempre.
  return status.breakStartAt?.toMillis() ?? status.breakStartedAt?.toMillis() ?? nowMs;
}

/**
 * Minutos que la pausa aporta al tiempo de espera.
 *
 * Si todavía no ha empezado, aporta su duración íntegra: la cola que hay por
 * delante ya se cuenta aparte, y tomar «fin menos ahora» la contaría dos veces.
 * Si está en curso, aporta lo que le queda. Terminada, no aporta nada.
 */
export function pendingBreakMinutes(status: BarberStatus | null | undefined, nowMs: number = Date.now()): number {
  const inicio = inicioMs(status, nowMs);
  if (inicio === null) return 0;
  const fin = status!.breakExpectedEndAt!.toMillis();
  if (fin <= nowMs) return 0;
  return Math.ceil((fin - Math.max(inicio, nowMs)) / 60000);
}

/** Lo mismo en segundos, para los contadores que trabajan a esa resolución. */
export function pendingBreakSeconds(status: BarberStatus | null | undefined, nowMs: number = Date.now()): number {
  const inicio = inicioMs(status, nowMs);
  if (inicio === null) return 0;
  const fin = status!.breakExpectedEndAt!.toMillis();
  if (fin <= nowMs) return 0;
  return Math.ceil((fin - Math.max(inicio, nowMs)) / 1000);
}

/** ¿Está el profesional comiendo AHORA, frente a tenerlo solo comprometido para más tarde? */
export function isBreakActive(status: BarberStatus | null | undefined, nowMs: number = Date.now()): boolean {
  const inicio = inicioMs(status, nowMs);
  if (inicio === null) return false;
  return inicio <= nowMs && status!.breakExpectedEndAt!.toMillis() > nowMs;
}

/** ¿Hay una pausa comprometida que todavía no ha empezado? */
export function isBreakScheduled(status: BarberStatus | null | undefined, nowMs: number = Date.now()): boolean {
  const inicio = inicioMs(status, nowMs);
  if (inicio === null) return false;
  return inicio > nowMs;
}

/** Hora de inicio en formato HH:MM, o null. */
export function breakStartLabel(status: BarberStatus | null | undefined, nowMs: number = Date.now()): string | null {
  const inicio = inicioMs(status, nowMs);
  if (inicio === null) return null;
  return new Date(inicio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

/** Hora de vuelta en formato HH:MM, o null. */
export function breakEndLabel(status: BarberStatus | null | undefined): string | null {
  if (!status?.breakExpectedEndAt) return null;
  return status.breakExpectedEndAt.toDate().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Minutos de pausa que soporta un turno que YA existe en la cola.
 *
 * Es la regla del servidor (`reorderQueuePositions`): la comida se ancla a la
 * cola que había al marcarla, de modo que un turno creado DESPUÉS va detrás de
 * ella y uno creado antes no. Se decide por fecha de creación, no estimando
 * minutos: comparar minutos fallaba porque navegador y servidor estiman la cola
 * con bases distintas —nominal de 30 frente a media histórica de unos 43— y
 * la comida desaparecía de la pantalla hasta que ya había empezado. Aviso del
 * negocio del 7-09-2026.
 *
 * - Pausa en curso: la sufre todo turno pendiente, por lo que le quede.
 * - Pausa programada: la sufre entera si el turno se creó después de marcarla.
 * - Sin fecha de creación o sin `breakStartedAt` (documentos anteriores a #22):
 *   se recurre a la comparación por minutos, que es lo que había.
 */
export function breakMinutesForTicket(
  status: BarberStatus | null | undefined,
  ticketCreatedAt: { toMillis(): number } | null | undefined,
  minutosHastaEmpezar: number,
  nowMs: number = Date.now()
): number {
  const duracion = pendingBreakMinutes(status, nowMs);
  if (duracion <= 0) return 0;
  if (isBreakActive(status, nowMs)) return duracion;
  const marcada = status?.breakStartedAt?.toMillis?.();
  const creado = ticketCreatedAt?.toMillis?.();
  if (marcada === undefined || creado === undefined) {
    return breakMinutesBefore(status, minutosHastaEmpezar, nowMs);
  }
  return creado > marcada ? duracion : 0;
}

/**
 * Minutos de pausa que soporta quien empiece a ser atendido dentro de
 * `minutosHastaEmpezar`.
 *
 * La pausa ocupa un tramo concreto de la jornada: quien entra a la silla antes
 * de que empiece no la sufre, y quien entra después la sufre entera. Es la
 * misma regla que `breakMinutesBefore` en el servidor.
 */
export function breakMinutesBefore(
  status: BarberStatus | null | undefined,
  minutosHastaEmpezar: number,
  nowMs: number = Date.now()
): number {
  const duracion = pendingBreakMinutes(status, nowMs);
  if (duracion <= 0) return 0;
  const inicio = inicioMs(status, nowMs)!;
  const minutosHastaLaPausa = Math.max(0, (inicio - nowMs) / 60000);
  return minutosHastaEmpezar >= minutosHastaLaPausa ? duracion : 0;
}
